import { Button } from "@/components/ui/FormControls";
import { EXCLUSION_LABEL, STAGE_LABEL, fmt, pct, type CalcResult, type Scenario } from "@h2lca/core";
import { EquipmentBarChart } from "@/components/charts/EquipmentBarChart";
import { ShareBar } from "@/components/charts/ShareBar";
import { StageBarChart } from "@/components/charts/StageBarChart";
import { InfoTip } from "@/components/ui/InfoTip";
import { useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { StageBadge, StatusBadge } from "../Badges";
import { GlossaryTip } from "../GlossaryTip";
import { DENOMINATOR_LABEL } from "../helpers";
import { NumField } from "../NumField";
import { Block, Section } from "../Section";
import { RunSavePanel } from "@/components/workspace/RunSavePanel";

const DISCLAIMER = "본 결과는 모의산정 결과로서 종합적인 환경영향평가, 제3자 검증 또는 청정수소 인증기관의 공식 판정을 대체하지 않습니다.";

/** ⑦ 결과 확인 , 총량,기능단위당, 단계별,설비별 그래프와 표, 기여도 상위 항목의 값 바꿔 보기, 인증 등급, 기준결과값 비교 */
export function ResultStep({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const data = useMaster((s) => s.data);
  const setStep = useWorkshop((s) => s.setStep);
  const select = useWorkshop((s) => s.select);
  const updateInput = useWorkshop((s) => s.updateInput);
  const example = scenario.exampleId ? data.examples.find((e) => e.id === scenario.exampleId) : undefined;
  const reference = example?.reference ?? null;
  const errors = result.issues.filter((i) => i.level === "error");

  return (
    <Section
      title={
        <>
          결과 확인 <GlossaryTip k="co2eq" />
        </>
      }
      desc="설비별, 단계별 배출량과 기능단위당 배출량입니다. 값을 바꾸면 즉시 다시 계산됩니다."
      aside={<StatusBadge status={result.status} />}
    >
      {result.status === "blocked" && (
        <div className="note note-danger flex items-center justify-between gap-item text-caption">
          <span>필수값 누락 또는 오류 {errors.length}건이 있어 최종 결과(기능단위당 배출량)를 산출하지 않습니다.</span>
          <Button type="button" size="compact" onClick={() => setStep("calc")}>
            검증 메시지 보기
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-content xl:grid-cols-4">
        <Stat label="1 kg당 배출량" value={result.perKgH2 === null ? "없음" : fmt(result.perKgH2, 3)} unit="kgCO2eq/kgH2" big />
        <Stat label="총 온실가스 배출량" value={fmt(result.totalKgCO2eq)} unit="kgCO2eq" />
        <Stat label={DENOMINATOR_LABEL[result.denominator.kind]} value={fmt(result.denominator.kg)} unit="kg" />
        <Stat label={`기능단위 수소 ${fmt(result.functionalUnitKg)} kg의 배출량`} value={result.perFunctionalUnitKgCO2eq == null ? "없음" : fmt(result.perFunctionalUnitKgCO2eq, 3)} unit="kgCO2eq/FU" />
      </div>

      <div className="ws-paired-panels">
        <Block title="단계별 배출량">
          <StageBarChart byStage={result.byStage} />
        </Block>
        <Block title="설비별 배출량">
          <EquipmentBarChart byEquipment={result.byEquipment} />
        </Block>
      </div>

      <Block title="기여도(설비별 비율)">
        <ShareBar byEquipment={result.byEquipment} />
      </Block>

      <Block title="설비별 배출량 표">
        <div className="table-scroll"><table className="ws-table">
          <thead>
            <tr>
              <th>설비</th>
              <th>단계</th>
              <th className="num">kgCO2eq</th>
              <th className="num">비율</th>
            </tr>
          </thead>
          <tbody>
            {result.byEquipment.map((e) => (
              <tr key={e.equipmentId} data-muted={!e.inBoundary}>
                <td>
                  {e.name}
                  {!e.inBoundary && <span className="ml-label badge-soft">제외, {e.exclusionReason ? EXCLUSION_LABEL[e.exclusionReason] : "경계 밖"}</span>}
                </td>
                <td>
                  <StageBadge stage={e.stage} />
                </td>
                <td className="num">{e.inBoundary ? fmt(e.kgCO2eq) : "없음"}</td>
                <td className="num">{e.inBoundary ? pct(e.share) : "없음"}</td>
              </tr>
            ))}
            {result.byStage.map((s) => (
              <tr key={s.stage} className="bg-surface-inset">
                <td className="font-semibold">{STAGE_LABEL[s.stage]} 소계</td>
                <td />
                <td className="num font-semibold">{fmt(s.kgCO2eq)}</td>
                <td className="num">{pct(s.share)}</td>
              </tr>
            ))}
            <tr>
              <td className="font-bold">총계</td>
              <td />
              <td className="num font-bold">{fmt(result.totalKgCO2eq)}</td>
              <td className="num">100%</td>
            </tr>
          </tbody>
        </table></div>
      </Block>

      <Block
        title={
          <>
            기여도 상위 항목. 값 바꿔 보기
            <InfoTip label="값 바꿔 보기 설명">배출량이 큰 입력값 다섯 개입니다. 여기서 값을 고치면 시나리오에 그대로 반영되고(값 유형은 사용자 입력으로 바뀜) 결과가 즉시 갱신됩니다.</InfoTip>
          </>
        }
      >
        {result.topInputs.length === 0 ? (
          <p className="text-footnote text-ink-subtle">계산된 항목이 없습니다.</p>
        ) : (
          <ul className="flex flex-col gap-item">
            {result.topInputs.map((c) => {
              const eq = scenario.equipment.find((e) => e.id === c.equipmentId);
              const input = eq?.inputs.find((i) => i.id === c.id);
              if (!eq || !input) return null;
              return (
                <li key={c.id} className="grid grid-cols-1 items-end gap-item rounded-card border border-line-soft bg-panel p-item sm:grid-cols-3">
                  <div className="min-w-0">
                    <p className="truncate text-caption font-semibold text-ink">{c.label}</p>
                    <p className="font-mono text-footnote text-ink-muted">
                      {fmt(c.kgCO2eq)} kgCO2eq, {pct(c.share)}
                    </p>
                  </div>
                  <NumField label="값" unit={input.unit} value={input.amount} min={0} onChange={(v) => updateInput(eq.id, input.id, { amount: v })} />
                  <span className="justify-self-start sm:justify-self-end"><Button
                    type="button"
                    onClick={() => {
                      select(eq.id);
                      setStep("inputs");
                    }}
                  >
                    운전자료에서 보기
                  </Button></span>
                </li>
              );
            })}
          </ul>
        )}
      </Block>

      {result.cert && (
        <Block title="청정수소 인증제 모의 판정">
          {result.cert.eligible ? (
            <div className="flex flex-wrap items-center gap-content">
              <span className={`note ${result.cert.grade !== null ? "note-ok" : "note-warn"} text-body`}>
                {result.cert.grade !== null ? (
                  <>
                    <b className="font-mono text-figure">{result.cert.grade}등급</b>, {fmt(result.perKgH2, 3)} kgCO2eq/kgH2
                  </>
                ) : result.perKgH2 === null ? (
                  "결과가 산출되면 등급을 판정합니다"
                ) : (
                  <>
                    청정수소 기준 초과: {fmt(result.perKgH2, 3)} &gt; {result.cert.thresholdKgCO2eq} kgCO2eq/kgH2
                  </>
                )}
              </span>
              <span className="text-footnote text-ink-subtle">재생에너지 비율 {pct(result.cert.renewableShare)} (단순화 적용)</span>
            </div>
          ) : (
            <ul className="flex flex-col gap-label">
              {result.cert.reasons.map((r) => (
                <li key={r} className="note note-danger text-caption">
                  인증제 모의산정 조건 미충족: {r}
                </li>
              ))}
            </ul>
          )}
          <div className="table-scroll"><table className="ws-table ws-table-compact">
            <thead>
              <tr>
                <th>등급</th>
                <th className="num">기준 (kgCO2eq/kgH2 이하)</th>
              </tr>
            </thead>
            <tbody>
              {result.cert.grades.map((g) => (
                <tr key={g.grade} className={result.cert?.grade === g.grade ? "bg-accent-wash" : ""}>
                  <td>{g.grade}등급</td>
                  <td className="num">{g.maxKgCO2eq}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Block>
      )}

      <Block title="기준 시나리오와 비교">
        {example ? (
          reference ? (
            <dl className="ws-kv">
              <dt>예시</dt>
              <dd className="font-sans">{example.name}</dd>
              <dt>기준결과값</dt>
              <dd>
                {fmt(reference.perKgH2, 3)} kgCO2eq/kgH2 <span className="text-ink-muted">(총 {fmt(reference.totalKgCO2eq)} kgCO2eq / {fmt(reference.denominatorKg)} kg)</span>
              </dd>
              <dt>계산값</dt>
              <dd>{result.perKgH2 === null ? "없음" : `${fmt(result.perKgH2, 3)} kgCO2eq/kgH2`}</dd>
              <dt>차이</dt>
              <dd>{result.perKgH2 === null ? "없음" : `${result.perKgH2 - reference.perKgH2 >= 0 ? "+" : ""}${fmt(result.perKgH2 - reference.perKgH2, 3)} kgCO2eq/kgH2 (${reference.perKgH2 > 0 ? pct((result.perKgH2 - reference.perKgH2) / reference.perKgH2) : "없음"})`}</dd>
              {reference.note && (
                <>
                  <dt>비고</dt>
                  <dd className="font-sans">{reference.note}</dd>
                </>
              )}
            </dl>
          ) : (
            <p className="text-caption text-ink-muted">
              예시 <b className="text-ink">{example.name}</b> 의 기준결과값은 전문가 검산 후 제공됩니다.
            </p>
          )
        ) : (
          <p className="text-caption text-ink-muted">예시에서 출발한 시나리오가 아니라 비교할 기준결과값이 없습니다.</p>
        )}
      </Block>

      <RunSavePanel scenario={scenario} />

      <p className="text-footnote leading-relaxed text-ink-subtle">{DISCLAIMER} CSV 내려받기와 결과보고서는 화면 상단의 ‘내보내기’에서 사용할 수 있습니다.</p>
    </Section>
  );
}

function Stat({ label, value, unit, big = false }: { label: string; value: string; unit: string; big?: boolean }) {
  return (
    <div className={`rounded-card border border-line-soft p-content ${big ? "bg-accent-wash" : "bg-panel"}`}>
      <p className="text-micro text-ink-muted">{label}</p>
      <p className={`mt-label font-mono font-bold text-ink ${big ? "text-metric" : "text-figure"}`}>{value}</p>
      <p className="text-micro text-ink-subtle">{unit}</p>
    </div>
  );
}
